import React, { useContext, useEffect, useState, useMemo } from 'react';
import AuthContext from '../context/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import Base from '../components/Base';
import axios from 'axios';
import { Card, Button, Input, Textarea, Select, Alert, Spinner } from './ui';
import API_BASE_URL from '../config/api';

import { devLog } from '../utils/devLog';
const MAX_FILE_SIZE_MB = 3;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const API_BASE_URL_FOR_IMAGES = API_BASE_URL;


// Reusable Image Upload Field Component
const ImageUploadField = ({
  id,
  label,
  value, // {file: File, preview: string, originalUrl: string, error: string}
  onChange, // (value: {file: File, preview: string}, error: string) => void
  onDelete, // () => void
  error,
  showRequiredStar = true,
  altText = "Preview",
  className = "w-36 h-48 sm:w-40 sm:h-52", // Default for customization images, can be overridden
}) => {
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > MAX_FILE_SIZE_BYTES) {
        // Pass original preview if file is too large, so existing image isn't lost
        onChange({ file: null, preview: value?.preview || null }, `File size must be ${MAX_FILE_SIZE_MB}MB or less.`);
        e.target.value = ''; // Clear file input value to allow re-selection of same file after error
        return;
      }
      const previewUrl = URL.createObjectURL(file); // Create a blob URL for immediate preview
      onChange({ file, preview: previewUrl }, null); // Pass new file and its preview to parent
    }
  };

  const handleDeleteClick = (e) => {
    e.stopPropagation(); // Prevent triggering parent div's onClick
    onDelete(); // Notify parent to delete
    const inputElement = document.getElementById(id);
    if (inputElement) inputElement.value = ''; // Clear file input value
  };

  useEffect(() => {
    let currentPreview = value?.preview;
    // Cleanup function: revoke blob URL when component unmounts or preview changes
    return () => {
      if (currentPreview && currentPreview.startsWith('blob:')) {
        URL.revokeObjectURL(currentPreview);
      }
    };
  }, [value?.preview]); // Dependency array: run effect when value.preview changes


  return (
    <div className="mb-4">
      {label && (
        <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">
          {label} {showRequiredStar && <span className="text-red-500">*</span>}
        </label>
      )}
      <div
        className={`${className} border-2 border-dashed border-gray-300 flex items-center justify-center relative cursor-pointer hover:border-orange-500 bg-gray-50 rounded-md`}
        onClick={() => document.getElementById(id)?.click()} // Simulate click on hidden file input
      >
        <input
          type="file"
          id={id}
          accept="image/*" // Accept all image types
          className="hidden" // Hide the default file input
          onChange={handleFileChange}
        />
        {value?.preview ? ( // If there's a preview URL, show the image
          <>
            <img src={value.preview} alt={altText} className="w-full h-full object-cover rounded-md" />
            <button
              type="button"
              onClick={handleDeleteClick}
              className="absolute top-1 right-1 bg-white text-red-500 p-1 rounded-full shadow hover:bg-red-100 text-xl leading-none"
              aria-label="Delete image"
            >
              &times;
            </button>
          </>
        ) : ( // Otherwise, show "Upload Image" text
          <span className="text-gray-500 text-center px-2 text-sm">Upload Image</span>
        )}
      </div>
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
  );
};


const EditProduct = () => {
  const navigate = useNavigate();
  const { productId } = useParams(); // Get product ID from URL parameter
  const { authToken } = useContext(AuthContext);

  // Memoized Axios instance for authenticated requests, recreating only if token changes
  const authAxios = useMemo(() => axios.create({
    baseURL: `${API_BASE_URL}/api`,
    headers: {
      Authorization: `Bearer ${authToken?.access}`,
    },
  }), [authToken?.access]);

  // Defines the initial structure for a single customization option.
  // `originalUrl` is crucial here to track if an image was pre-existing (from backend)
  // and is then explicitly removed by the user (by setting `preview` to null).
  const initialCustomizationOption = () => ({
    name: '',
    image: { file: null, preview: null, originalUrl: null }, // 'file' for new upload, 'preview' for display, 'originalUrl' for backend image
    price: '',
    imageError: null,
  });

  // Defines the full initial form structure, including placeholders for all images and customizations.
  // Memoized to prevent re-creation on every render if dependencies don't change.
  const initialFormData = useMemo(() => ({
    name: '',
    description: '',
    gender: '',
    type: '',
    category: '',
    fabric: '',
    color: '',
    price: '',
    discountedPrice: '',
    discountPercent: 0,
    days: '',
    stockQuantity: '',
    disclaimer: '',
    compositionAndCare: '',
    is_customizable: 'no', // Boolean stored as 'yes'/'no' string for radio buttons
    custom_description: '', // UI-only field for notes, not saved to backend Product model
    mainImages: Array(6).fill(null).map(() => ({ file: null, preview: null, error: null, originalUrl: null })), // Main product images
    customizations: {
      type1: Array(3).fill(null).map(initialCustomizationOption), // Fabric options
      type2: Array(3).fill(null).map(initialCustomizationOption), // Border options
      type3: Array(3).fill(null).map(initialCustomizationOption), // Border Design options
    },
  }), []); // Empty dependency array means it's created once

  // State variables for the form
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({}); // Stores validation errors for form fields
  const [typeOptions, setTypeOptions] = useState([]); // Dynamic options for product type
  const [categoryOptions, setCategoryOptions] = useState([]); // Dynamic options for product category
  const [isLoading, setIsLoading] = useState(true); // Tracks initial data loading
  const [productNotFound, setProductNotFound] = useState(false); // Flag if product doesn't exist/user lacks permission


  // Effect to fetch product data from backend on component mount or productId/authToken change
  useEffect(() => {
    const fetchProductData = async () => {
      if (!authToken?.access || !productId) {
        setIsLoading(false); 
        if (!authToken?.access) navigate('/login'); // Redirect if no auth token
        return;
      }

      // Show loading spinner using SweetAlert
      Swal.fire({
        title: 'Loading Product Data...',
        text: 'Please wait.',
        allowOutsideClick: false, // Prevent closing by clicking outside
        didOpen: () => Swal.showLoading(), // Show loading spinner within the dialog
      });

      try {
        // Make GET request to fetch product data for the given productId
        const response = await authAxios.get(`/edit_product/${productId}/`);
        const { product: productData } = response.data;

        // Populate main images from fetched productData.main_image_urls
        const loadedMainImages = initialFormData.mainImages.map((_, i) => {
          const imgUrl = productData.main_image_urls?.[`img${i + 1}`]; // Get URL for img1, img2, etc.
          if (imgUrl) {
            // Construct full URL if it's a relative path
            const fullUrl = imgUrl.startsWith('http') || imgUrl.startsWith('/') ? imgUrl : `${API_BASE_URL_FOR_IMAGES}${imgUrl}`;
            // file: null means no new file selected yet, preview: fullUrl for display, originalUrl: fullUrl to track original
            return { file: null, preview: fullUrl, originalUrl: fullUrl, error: null };
          }
          return { file: null, preview: null, originalUrl: null, error: null }; // No image
        });

        // Customizations (fabric/border/borderDesign options) have been removed from the backend.
        // Initialize with empty structure.
        let loadedCustomizations = JSON.parse(JSON.stringify(initialFormData.customizations));

        // Update form state with fetched product data
        setFormData(prev => ({
          ...prev, // Keep existing state, but overwrite with fetched data
          name: productData.name || '',
          description: productData.description || '',
          gender: productData.gender || '',
          type: productData.type || '',
          category: productData.category || '',
          fabric: productData.fabric || '',
          color: productData.color || '',
          price: productData.price !== null ? String(productData.price) : '',
          discountedPrice: productData.Discountedprice !== null ? String(productData.Discountedprice) : '',
          discountPercent: productData.Discountpercent !== null ? parseFloat(productData.Discountpercent).toFixed(2) : 0,
          days: productData.days !== null ? String(productData.days) : '',
          stockQuantity: productData.stock_quantity !== null && productData.stock_quantity !== undefined ? String(productData.stock_quantity) : '',
          disclaimer: productData.disclaimer || '',
          compositionAndCare: productData.compositionAndCare || '', 
          is_customizable: productData.is_customizable ? 'yes' : 'no', // Convert boolean to 'yes'/'no' string
          mainImages: loadedMainImages, // Set loaded main images
          customizations: loadedCustomizations, // Set loaded customization options
          custom_description: '', // This is a UI-only field, not stored on backend Product model
        }));

        Swal.close(); // Close loading spinner
        setIsLoading(false); // Mark loading as complete
      } catch (error) {
        Swal.close(); // Close loading spinner
        devLog.error('Error fetching product data:', error.response || error.message || error);
        if (error.response && error.response.status === 404) {
          setProductNotFound(true); // Set flag if product is not found
          Swal.fire('Error', 'Product not found.', 'error');
        } else {
          Swal.fire('Error', `Failed to load product data: ${error.message || 'Check console for details'}`, 'error');
        }
        setIsLoading(false); // Mark loading as complete even on error
        // Do not navigate on error here, allow productNotFound state to show message first.
        // navigate('/homepage'); // Redirect on severe error or product not found
      }
    };

    fetchProductData();
  }, [productId, authAxios, authToken?.access, navigate, initialFormData]); // Dependencies: re-run if these change

  // Effect to update Type options based on Gender selection
  useEffect(() => {
    if (formData.gender) {
      let options = [];
      if (formData.gender === 'Men') options = ['Indianwear', 'Westernwear'];
      else if (formData.gender === 'Women') options = ['Indianwear', 'Westernwear'];
      else if (formData.gender === 'Kids') options = ['Boys', 'Girls'];
      setTypeOptions(options);
      // The logic to reset the type and category is correctly handled in handleInputChange
    } else {
      setTypeOptions([]);
      setCategoryOptions([]);
    }
  }, [formData.gender]);

  // Effect to update Category options based on Gender and Type selection
  useEffect(() => {
    if (formData.gender && formData.type) {
      let categories = [];
      const MenIndian = ['Sherwanis', 'Bandhgalas', 'Kurtas', 'Kurta Sets', 'Nehru Jackets', 'Bandi Jackets', 'Dhoti Kurta', 'Pathani Suits', 'Jodhpuri Suits'];
      const MenWestern = ['Blazers', 'Casual Shirts', 'Formal Shirts', 'Over Sized Shirts', 'Suits and Tuxedos', 'Coats and Jackets', 'Trousers and Pants', 'Sleepwear'];
      const WomenIndian = ['Kurtis', 'Kurti sets', 'Blouses', 'Tunics', 'Shararas', 'Ghagras', 'Sharara and Gharara Sets', 'Lehengas', 'Sarees', 'Sarees sets', 'Suits', 'Salwar Suits', 'Anarkali Suits', 'Straight Cut Suits', 'Salwar Kameez', 'Dupattas', 'Indo-Western', 'Indo-Western Gowns', 'Capes and Overlays', 'Kaftans', 'Gowns', 'Trousers and Bottoms', 'Palazzos', 'Jackets'];
      const WomenWestern = ['Crop-Tops', 'Casual Tops', 'Dresses', 'Gowns', 'Tops and Blouses', 'Co-Ord Sets', 'Saree Gowns', 'Dhoti Pants', 'Jackets', 'Blazers', 'Jumpsuits and Playsuits', 'Trousers', 'Pants and Bottoms', 'Skirts', 'Jumpsuits', 'Sleepwear'];
      const KidsBoys = ['Top Wear', 'Bottom Wear', 'Co-Ord Sets'];
      const KidsGirls = ['Top Wear', 'Bottom Wear', 'Short Frocks', 'Long Frocks', 'Co-Ord Sets'];

      if (formData.gender === 'Men') categories = formData.type === 'Indianwear' ? MenIndian : MenWestern;
      else if (formData.gender === 'Women') categories = formData.type === 'Indianwear' ? WomenIndian : WomenWestern;
      else if (formData.gender === 'Kids') categories = formData.type === 'Boys' ? KidsBoys : KidsGirls;
      setCategoryOptions(categories);
      // The logic to reset the category is correctly handled in handleInputChange
    } else {
      setCategoryOptions([]);
    }
  }, [formData.gender, formData.type]);

  // Handle generic input changes for form fields
  const handleInputChange = async (e) => {
    const { name, value, type } = e.target;
    // Special handling for 'is_customizable' radio buttons
    if (type === 'radio' && name === 'is_customizable') {
      setFormData((prev) => ({ ...prev, is_customizable: value }));
    } else {
      // For other input fields, just update the formData state
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    // Reset dependent dropdowns if parent changes
    if (name === 'gender') {
        setFormData(prev => ({ ...prev, type: '', category: ''}));
        setCategoryOptions([]);
    } else if (name === 'type') {
        setFormData(prev => ({ ...prev, category: ''}));
    }

    // Clear error for the field if user starts typing (optimistic error clearing)
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  // Handles changes for main product images (file input or clearing)
  const handleMainImageChange = (index, imageValue, errorMsg) => {
    setFormData(prev => {
      const newImages = [...prev.mainImages];
      const oldPreview = newImages[index]?.preview;
      // Revoke old blob URL if it exists and is different from the new preview (prevent memory leaks)
      if (oldPreview && oldPreview.startsWith('blob:') && oldPreview !== imageValue.preview) {
        URL.revokeObjectURL(oldPreview);
      }
      newImages[index] = {
        ...newImages[index], // Keep originalUrl if it exists
        file: imageValue.file, // New file object (or null)
        preview: imageValue.preview, // New preview URL (blob or direct URL)
        error: errorMsg
      };
      return { ...prev, mainImages: newImages };
    });
    // Clear mainImages error if applicable
    if (errors.mainImages) setErrors(prev => ({ ...prev, mainImages: null }));
  };

  // Handles deletion of a main product image
  const handleMainImageDelete = (index) => {
    setFormData(prev => {
      const newImages = [...prev.mainImages];
      const oldPreview = newImages[index]?.preview;
      if (oldPreview && oldPreview.startsWith('blob:')) {
        URL.revokeObjectURL(oldPreview); // Revoke old blob URL
      }
      // Set file to null, preview to null (to signal deletion to backend)
      newImages[index] = { ...newImages[index], file: null, preview: null, error: null };
      return { ...prev, mainImages: newImages };
    });
  };

  // Calculates discount percentage based on price and discountedPrice
  useEffect(() => {
    const price = parseFloat(formData.price);
    const discountedPrice = parseFloat(formData.discountedPrice);
    if (!isNaN(price) && price > 0 && !isNaN(discountedPrice) && discountedPrice > 0 && discountedPrice < price) {
      const discount = ((price - discountedPrice) / price) * 100;
      setFormData(prev => ({ ...prev, discountPercent: discount.toFixed(2) }));
    } else if (formData.discountedPrice === '' || isNaN(discountedPrice) || discountedPrice <= 0 || (price > 0 && discountedPrice >= price)) {
      setFormData(prev => ({ ...prev, discountPercent: 0 }));
    }
  }, [formData.price, formData.discountedPrice]);

  // Validates the entire form before submission
  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Product name is required.';
    if (!formData.description.trim()) newErrors.description = 'Description is required.';
    if (!formData.gender) newErrors.gender = 'Gender is required.';
    if (!formData.type && formData.gender) newErrors.type = 'Type is required.';
    if (!formData.category && formData.type) newErrors.category = 'Category is required.';
    if (!formData.fabric.trim()) newErrors.fabric = 'Fabric is required.';
    if (!formData.color.trim()) newErrors.color = 'Color is required.';

    if (!formData.price.trim()) newErrors.price = 'Price is required.';
    else if (isNaN(parseFloat(formData.price)) || parseFloat(formData.price) <= 0) newErrors.price = 'Enter a valid positive price.';

    if (formData.discountedPrice.trim()) {
      if (isNaN(parseFloat(formData.discountedPrice)) || parseFloat(formData.discountedPrice) <= 0) {
        newErrors.discountedPrice = 'Enter a valid positive discounted price.';
      } else if (parseFloat(formData.price) > 0 && parseFloat(formData.discountedPrice) >= parseFloat(formData.price)) {
        newErrors.discountedPrice = 'Discounted price must be less than the original price.';
      }
    }

    if (!formData.days) newErrors.days = 'Days to complete is required.';
    if (!formData.disclaimer.trim()) newErrors.disclaimer = 'Disclaimer is required.';
    if (!formData.compositionAndCare.trim()) newErrors.compositionAndCare = 'Composition and Care is required.';

    // Check for at least one main image (new file or existing preview)
    const hasAtLeastOneMainImage = formData.mainImages.some(img => img && (img.file || img.preview));
    if (!hasAtLeastOneMainImage) {
      newErrors.mainImages = 'At least one product image is required.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0; // Return true if no errors
  };


  // Handles form submission (update operation)
  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) {
      Swal.fire('Validation Error', 'Please correct the errors in the form.', 'error');
      // Scroll to the first error element for better UX
      const firstErrorElement = document.querySelector('.border-red-500, .text-red-500');
      if (firstErrorElement) {
        firstErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' }); // Scroll to top if no specific error element found
      }
      return;
    }

    // Show SweetAlert loading/progress dialog
    Swal.fire({ title: 'Updating Product...', text: 'Please wait.', allowOutsideClick: false, didOpen: () => Swal.showLoading() });

    const payload = new FormData(); // Use FormData for multipart/form-data with files and text
    // Append all basic product fields
    payload.append('name', formData.name);
    payload.append('description', formData.description);
    payload.append('gender', formData.gender);
    payload.append('type', formData.type);
    payload.append('category', formData.category);
    payload.append('fabric', formData.fabric);
    payload.append('color', formData.color);
    payload.append('price', formData.price);
    // Only append if not empty, backend handles None
    if (formData.discountedPrice.trim()) payload.append('Discountedprice', formData.discountedPrice);
    if (parseFloat(formData.discountPercent) > 0) payload.append('Discountpercent', formData.discountPercent);
    payload.append('days', formData.days);
    if (formData.stockQuantity !== '') payload.append('stock_quantity', formData.stockQuantity);
    payload.append('disclaimer', formData.disclaimer);
    payload.append('compositionAndCare', formData.compositionAndCare);
    // Convert 'yes'/'no' to '1'/'0' for backend boolean field (`is_customizable`)
    payload.append('customizationPossible', formData.is_customizable === 'yes' ? '1' : '0'); // Backend expects 'customizationPossible'


    // Handle main images for update: send new files or explicit deletion signals
    formData.mainImages.forEach((imgData, index) => {
      const fieldName = `img${index + 1}`; // img1, img2, etc.
      // 1. If a new file has been selected (file exists)
      if (imgData.file) { 
        payload.append(`${fieldName}_file`, imgData.file, imgData.file.name); // Append with '_file' suffix
      } 
      // 2. If preview is null but originalUrl existed, it means user explicitly cleared/deleted it
      else if (!imgData.preview && imgData.originalUrl) { 
        payload.append(`${fieldName}_delete`, 'true'); // Send '_delete' flag
      }
      // 3. If imgData.preview exists and imgData.file is null, and originalUrl exists and matches preview: no change, so no payload needed.
      // This implicitly tells backend to keep existing image.
    });

    // Customization: only send the flag, don't touch existing customization data
    // When "No", tell backend to clear customization data
    if (formData.is_customizable !== 'yes') {
      const backendItemTypePrefixes = ["fabric", "Border", "BorderDesign"];
      backendItemTypePrefixes.forEach((backendPrefix) => {
        for (let i = 1; i <= 3; i++) {
          payload.append(`${backendPrefix}Img${i}_delete`, 'true');
        }
      });
      payload.append('customizations_json', JSON.stringify({ type1: [], type2: [], type3: [] }));
    }

    try {
      // Send PATCH request for partial updates
      const response = await authAxios.patch(`/edit_product/${productId}/`, payload, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => { // Show upload progress in SweetAlert
              const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              Swal.update({
                  html: `
                      <p>Uploading and processing...</p>
                      <div style="width: 100%; background-color: #f3f3f3; border-radius: 5px; overflow: hidden; margin-top: 10px;">
                          <div style="width: ${percentCompleted}%; height: 20px; background-color: #f97316; text-align: center; color: white; line-height: 20px; border-radius: 5px;">${percentCompleted}%</div>
                      </div>
                      <p style="margin-top: 10px; font-size: 0.9em; color: #555;">${percentCompleted === 100 ? "Processing on server..." : `Uploading: ${percentCompleted}%`}</p>
                  `
              });
          }
      });
      Swal.close(); // Close the progress dialog after response is received

      if (response.status === 200 && response.data.success) { // HTTP 200 OK for success
        Swal.fire({
          icon: 'success', title: 'Product Updated!', text: response.data.message || 'Your product has been updated.',
          confirmButtonText: 'Go to Home',
        }).then(() => {
           navigate('/homepage'); // Navigate to homepage after successful update
        });
      } else {
        // Handle non-200 but successful response (e.g., backend returns 200 with error message)
        Swal.fire('Update Failed', response.data?.error || response.data?.details || 'An unexpected error occurred.', 'error');
      }
    } catch (error) {
      Swal.close(); // Close loading dialog on error
      devLog.error('Error updating product:', error.response || error.message || error);
      let errorMessage = 'An error occurred while updating the product.';
       if (error.response?.data) {
        const errorData = error.response.data;
        if (errorData.error) errorMessage = errorData.error + (errorData.details ? ` (Details: ${JSON.stringify(errorData.details)})` : '');
        else if (typeof errorData === 'string') errorMessage = errorData;
        else if (errorData.detail) errorMessage = typeof errorData.detail === 'string' ? errorData.detail : JSON.stringify(errorData.detail);
        else if (typeof errorData === 'object' && errorData !== null) {
            // Format generic object errors nicely
            errorMessage = Object.entries(errorData).map(([key, value]) => {
                let valStr = Array.isArray(value) ? value.join(', ') : String(value);
                if (valStr.length > 100) valStr = valStr.substring(0, 97) + "..."; // Truncate long messages
                return `${key}: ${valStr}`;
            }).join('; ');
            if (!errorMessage) errorMessage = JSON.stringify(errorData); // Fallback to stringify if still empty
        }
        if (errorMessage.length > 300) errorMessage = "Multiple validation errors. Please check form fields or console for details."; // Shorten very long messages
      } else if (error.message) errorMessage = error.message; // Generic network error
      Swal.fire('Update Failed', errorMessage, 'error');
      if (error.response?.status === 401) navigate('/login'); // Redirect to login on 401 Unauthorized
    }
  };

  const dayOptions = Array.from({ length: 28 }, (_, i) => i + 1); // Generates 1 to 28 days
  // Helper to dynamically apply input styling and error classes
  const getInputClass = (fieldName) => `w-full p-3 border rounded-md shadow-sm focus:ring-orange-500 focus:border-orange-500 bg-gray-50 text-base ${errors[fieldName] ? 'border-red-500' : 'border-gray-300'}`;
  // Global cleanup effect: revoke all blob URLs when component unmounts
  useEffect(() => {
    // Return a cleanup function
    return () => {
      // Iterate through main images and revoke any blob URLs
      formData.mainImages.forEach(imgData => {
        if (imgData?.preview && imgData.preview.startsWith('blob:')) {
          URL.revokeObjectURL(imgData.preview);
        }
      });
      // Iterate through customization images and revoke any blob URLs
      Object.values(formData.customizations).forEach(typeOptions => {
        typeOptions.forEach(option => {
          if (option.image?.preview && option.image.preview.startsWith('blob:')) {
            URL.revokeObjectURL(option.image.preview);
          }
        });
      });
    };
  }, [formData]); // Dependency on formData to ensure cleanup of all generated previews

  // Render loading state while product data is being fetched
  if (isLoading) {
    return <Base Content={<div className="flex justify-center items-center h-screen"><div className="text-xl">Loading product data...</div></div>} />;
  }
  // Render product not found state if API returns 404
  if (productNotFound) {
    return <Base Content={<div className="text-center py-10 text-red-500 text-xl">Product not found or you do not have permission to view it.</div>} />;
  }

  return (
    <div className="min-h-screen font-lora text-gray-800">
      <Base Content={
        <div className="container mx-auto px-2 sm:px-4 py-8 max-w-7xl">
          <h1 className="text-2xl md:text-3xl text-center font-bold text-[#d45614] mb-2">
            Edit Product
          </h1>
          <p className="text-center text-sm text-gray-600 mb-1">Product ID: {productId}</p>
          <div className="w-full h-px bg-gray-300 my-4"></div>

          <form onSubmit={handleSubmit} className="rounded-lg p-4 sm:p-8 bg-white shadow-xl">
            {/* Product Images Section */}
            <div className="mb-8 p-4 border border-gray-200 rounded-md bg-gray-50/50">
              <label className="block text-lg font-medium text-gray-700 mb-3">
                Product Images (Up to 6, at least 1 required) <span className="text-red-500">*</span>
              </label>
              <div className="flex flex-wrap gap-3 sm:gap-4 justify-center sm:justify-start">
                {formData.mainImages.map((imgData, index) => (
                  <ImageUploadField
                    key={`mainImg-${index}`}
                    id={`mainImg${index + 1}`}
                    value={imgData} // Pass current image data
                    onChange={(value, error) => handleMainImageChange(index, value, error)} // Handle new selection
                    onDelete={() => handleMainImageDelete(index)} // Handle deletion
                    error={imgData?.error || (errors.mainImages && index === 0 ? errors.mainImages : '')} // Display error for the field
                    altText={`Product Image ${index + 1}`}
                    showRequiredStar={false}
                  />
                ))}
              </div>
              {errors.mainImages && typeof errors.mainImages === 'string' && <p className="text-red-500 text-sm mt-2">{errors.mainImages}</p>}
            </div>

            {/* Product Details Section */}
            <div className="mb-8 p-4 border border-gray-200 rounded-md bg-gray-50/50">
              <h3 className="text-xl font-semibold text-gray-700 mb-4">Product Details</h3>
              <div className="space-y-6">
                {[
                  { label: 'Name of the product', name: 'name', type: 'text' },
                  { label: 'Description', name: 'description', type: 'textarea', rows: 3 },
                ].map(field => (
                  <div key={field.name}>
                    <label htmlFor={field.name} className="block text-sm font-medium text-gray-700 mb-1">
                      {field.label} <span className="text-red-500">*</span>
                    </label>
                    {field.type === 'textarea' ? (
                      <textarea id={field.name} name={field.name} value={formData[field.name]} onChange={handleInputChange} rows={field.rows} className={getInputClass(field.name)} />
                    ) : (
                      <input type={field.type} id={field.name} name={field.name} value={formData[field.name]} onChange={handleInputChange} className={getInputClass(field.name)} />
                    )}
                    {errors[field.name] && <p className="text-red-500 text-xs mt-1">{errors[field.name]}</p>}
                  </div>
                ))}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label htmlFor="gender" className="block text-sm font-medium text-gray-700 mb-1">Gender <span className="text-red-500">*</span></label>
                    <select id="gender" name="gender" value={formData.gender} onChange={handleInputChange} className={getInputClass('gender')}>
                      <option value="">Select Gender</option><option value="Men">Men</option><option value="Women">Women</option><option value="Kids">Kids</option>
                    </select>
                    {errors.gender && <p className="text-red-500 text-xs mt-1">{errors.gender}</p>}
                  </div>
                  <div>
                    <label htmlFor="type" className="block text-sm font-medium text-gray-700 mb-1">Type <span className="text-red-500">*</span></label>
                    <select id="type" name="type" value={formData.type} onChange={handleInputChange} disabled={!formData.gender} className={getInputClass('type')}>
                      <option value="">Select Type</option>{typeOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                    {errors.type && <p className="text-red-500 text-xs mt-1">{errors.type}</p>}
                  </div>
                  <div>
                    <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">Category <span className="text-red-500">*</span></label>
                    <select id="category" name="category" value={formData.category} onChange={handleInputChange} disabled={!formData.type} className={getInputClass('category')}>
                      <option value="">Select Category</option>{categoryOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                    {errors.category && <p className="text-red-500 text-xs mt-1">{errors.category}</p>}
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="fabric" className="block text-sm font-medium text-gray-700 mb-1">Main Product Fabric <span className="text-red-500">*</span></label>
                    <input type="text" id="fabric" name="fabric" value={formData.fabric} onChange={handleInputChange} className={getInputClass('fabric')} />
                    {errors.fabric && <p className="text-red-500 text-xs mt-1">{errors.fabric}</p>}
                  </div>
                  <div>
                    <label htmlFor="color" className="block text-sm font-medium text-gray-700 mb-1">Color <span className="text-red-500">*</span></label>
                    <input type="text" id="color" name="color" value={formData.color} onChange={handleInputChange} className={getInputClass('color')} />
                    {errors.color && <p className="text-red-500 text-xs mt-1">{errors.color}</p>}
                  </div>
                </div>
              </div>
            </div>


            {/* Pricing Section */}
            <div className="mb-8 p-4 border border-gray-200 rounded-md bg-gray-50/50">
              <h3 className="text-xl font-semibold text-gray-700 mb-4">Pricing</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="price" className="block text-sm font-medium text-gray-700 mb-1">Price (₹) <span className="text-red-500">*</span></label>
                  <input type="text" id="price" name="price" value={formData.price} onChange={handleInputChange} placeholder="e.g. 1000" className={getInputClass('price')} />
                  {errors.price && <p className="text-red-500 text-xs mt-1">{errors.price}</p>}
                </div>
                <div>
                  <label htmlFor="discountedPrice" className="block text-sm font-medium text-gray-700 mb-1">Discounted Price (₹)</label>
                  <input type="text" id="discountedPrice" name="discountedPrice" value={formData.discountedPrice} onChange={handleInputChange} placeholder="e.g. 800" className={getInputClass('discountedPrice')} />
                  {errors.discountedPrice && <p className="text-red-500 text-xs mt-1">{errors.discountedPrice}</p>}
                </div>
              </div>
              {parseFloat(formData.discountPercent) > 0 && (
                <p className="mt-3 text-md font-semibold text-green-600">Calculated Discount: {formData.discountPercent}%</p>
              )}
            </div>


            {/* Customizations Section */}
            <div className="mb-8 p-4 border border-gray-200 rounded-md bg-gray-50/50">
              <h3 className="text-xl font-semibold text-gray-700 mb-4">Customizations</h3>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Are customizations possible for this product? <span className="text-red-500">*</span></label>
                <div className="flex items-center gap-x-6">
                  <label className="flex items-center text-sm"><input type="radio" name="is_customizable" value="yes" checked={formData.is_customizable === 'yes'} onChange={handleInputChange} className="h-4 w-4 text-[#d45614] border-gray-300 focus:ring-orange-500" /><span className="ml-2">Yes</span></label>
                  <label className="flex items-center text-sm"><input type="radio" name="is_customizable" value="no" checked={formData.is_customizable === 'no'} onChange={handleInputChange} className="h-4 w-4 text-[#d45614] border-gray-300 focus:ring-orange-500" /><span className="ml-2">No</span></label>
                </div>
                {errors.customizations && typeof errors.customizations === 'string' && (
                  <p className="text-red-500 text-sm mt-2">{errors.customizations}</p>
                )}
              </div>

              {formData.is_customizable === 'yes' && (
                <p className="text-sm text-green-700 mt-2 p-3 bg-green-50 border border-green-200 rounded-md">
                  Customization is enabled for this product. Customers will be able to request custom options.
                </p>
              )}
            </div>

             <div className="mb-8 p-4 border border-gray-200 rounded-md bg-gray-50/50">
              <h3 className="text-xl font-semibold text-gray-700 mb-4">Additional Information</h3>
              <div className="space-y-6">
                <div>
                  <label htmlFor="days" className="block text-sm font-medium text-gray-700 mb-1">Days to Complete <span className="text-red-500">*</span></label>
                  <select id="days" name="days" value={formData.days} onChange={handleInputChange} className={getInputClass('days')}>
                    <option value="">Select days</option>
                    {dayOptions.map(day => <option key={day} value={day}>{day} day{day > 1 ? 's' : ''}</option>)}
                  </select>
                  {errors.days && <p className="text-red-500 text-xs mt-1">{errors.days}</p>}
                </div>
                <div>
                  <label htmlFor="stockQuantity" className="block text-sm font-medium text-gray-700 mb-1">Stock Quantity</label>
                  <p className="text-xs text-gray-600 mb-2">How many units do you have in stock? Leave empty or 0 if you make to order.</p>
                  <input type="number" id="stockQuantity" name="stockQuantity" value={formData.stockQuantity} onChange={handleInputChange} min="0" className={getInputClass('stockQuantity')} placeholder="e.g., 10" />
                </div>
                {[
                  { label: 'Disclaimer', name: 'disclaimer', rows: 3 },
                  { label: 'Composition and Care', name: 'compositionAndCare', rows: 3 },
                ].map(field => (
                  <div key={field.name}>
                    <label htmlFor={field.name} className="block text-sm font-medium text-gray-700 mb-1">
                      {field.label} <span className="text-red-500">*</span>
                    </label>
                    <textarea id={field.name} name={field.name} value={formData[field.name]} onChange={handleInputChange} rows={field.rows} className={getInputClass(field.name)} />
                    {errors[field.name] && <p className="text-red-500 text-xs mt-1">{errors[field.name]}</p>}
                  </div>
                ))}
              </div>
            </div>

            <button type="submit" className="w-full mt-6 bg-[#d45614] hover:bg-[#a8430e] text-white font-semibold py-3 px-6 rounded-lg transition duration-150 ease-in-out">
              Update Product
            </button>
          </form>
        </div>
      } />
    </div>
  );
};

export default EditProduct;